import React, { useMemo, useState } from 'react';
import {
	canEditDayTracking,
	challengeCalendarDays,
	formatChallengeDayLabel,
	isDayChecked,
	isDayTrackingVisible,
	isFutureChallengeDay,
	dayTrackingProgressPercent,
	todayChallengeISODate
} from '../../utils/challengeDayTracking';
import { challengeDayTrackingBlockedClass, hintClass } from './challengeWireFormStyles';

const dayListScrollClass = 'mt-3 max-h-64 overflow-y-auto overscroll-contain pr-0.5';

function ProgressBar({ percent, checkedCount, totalDays }) {
	return (
		<div className="space-y-1.5">
			<div
				className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
				role="progressbar"
				aria-valuenow={checkedCount}
				aria-valuemin={0}
				aria-valuemax={totalDays}
				aria-label={`${checkedCount} of ${totalDays} days marked`}
			>
				<div
					className="h-full rounded-full bg-green-500 transition-all duration-300 ease-out dark:bg-green-400"
					style={{ width: `${percent}%` }}
				/>
			</div>
		</div>
	);
}

function DayCheckRow({ isoDate, idPrefix, label, checked, disabled, pending, isToday, onToggle }) {
	const checkboxId = `${idPrefix}-day-${isoDate}`;

	return (
		<li>
			<label
				htmlFor={checkboxId}
				className={`flex items-center gap-3 rounded-lg px-2 py-2 transition ${
					disabled
						? 'cursor-not-allowed opacity-50'
						: 'cursor-pointer hover:bg-white dark:hover:bg-dark-bg'
				} ${isToday ? 'ring-1 ring-inset ring-blue-200 dark:ring-blue-800 bg-white dark:bg-dark-bg dark:bg-opacity-40' : ''}`}
			>
				<span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
					<input
						id={checkboxId}
						type="checkbox"
						checked={checked}
						disabled={disabled || pending}
						onChange={(event) => onToggle(isoDate, event.target.checked)}
						className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 dark:border-gray-600 dark:bg-dark-bg dark:focus:ring-green-400"
					/>
					{pending && (
						<span
							className="pointer-events-none absolute inset-0 flex items-center justify-center"
							aria-hidden="true"
						>
							<span className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-green-600 dark:border-gray-600 dark:border-t-green-400" />
						</span>
					)}
				</span>
				<span className="flex min-w-0 flex-1 items-center justify-between gap-2 text-sm">
					<span className={`font-medium ${checked ? 'text-gray-900 dark:text-dark-text' : 'text-gray-700 dark:text-gray-300'}`}>
						{label}
					</span>
					{isToday && (
						<span className="shrink-0 rounded-full border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:border-blue-800 dark:bg-blue-900 dark:bg-opacity-40 dark:text-blue-200">
							Today
						</span>
					)}
				</span>
			</label>
		</li>
	);
}

export default function ChallengeDayTracking({
	challenge,
	onDayCheck,
	isDayCheckPending,
	readOnly = false
}) {
	const panelId = `challenge-day-tracking-${challenge.kind}-${challenge.id}`;
	const dayTracking = challenge?.day_tracking;
	const [expanded, setExpanded] = useState(false);

	const visible = isDayTrackingVisible(dayTracking);
	const editable = canEditDayTracking(dayTracking) && !readOnly;

	const calendarDays = useMemo(
		() => challengeCalendarDays(challenge.starts_on, challenge.ends_on),
		[challenge.starts_on, challenge.ends_on]
	);

	if (!visible) return null;

	const todayIso = todayChallengeISODate(dayTracking);
	const checkedCount = dayTracking.checked_count || 0;
	const totalDays = dayTracking.total_days || calendarDays.length;
	const progressPercent = dayTrackingProgressPercent(dayTracking);

	return (
		<div className="rounded-xl border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg">
			<button
				type="button"
				id={`${panelId}-trigger`}
				aria-expanded={expanded}
				aria-controls={`${panelId}-panel`}
				onClick={() => setExpanded((value) => !value)}
				className="flex w-full items-center gap-3 px-3 py-2 text-left transition hover:bg-gray-100 dark:hover:bg-dark-bg"
			>
				<span className="flex min-w-0 flex-1 items-center gap-2">
					<i
						className="fa-regular fa-calendar-check text-sm text-green-600 dark:text-green-400"
						aria-hidden="true"
					/>
					<span className="text-sm font-semibold text-gray-900 dark:text-dark-text">Daily progress</span>
				</span>
				<span className="inline-flex items-center gap-2 shrink-0">
					<span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-gray-700 shadow-sm dark:bg-dark-card dark:text-gray-200">
						{checkedCount}/{totalDays}
					</span>
					<i
						className={`fa-solid fa-chevron-${expanded ? 'up' : 'down'} text-[10px] text-gray-400 dark:text-gray-500`}
						aria-hidden="true"
					/>
				</span>
			</button>

			{expanded && (
				<div id={`${panelId}-panel`} role="region" aria-labelledby={`${panelId}-trigger`} className="border-t border-gray-200 px-3 pb-3 pt-3 dark:border-dark-border">
					<ProgressBar percent={progressPercent} checkedCount={checkedCount} totalDays={totalDays} />

					<p className={`mt-3 ${hintClass}`}>
						Mark each day as you complete it to finish the challenge.
					</p>

					{!dayTracking.ready_for_completion && dayTracking.blocked_message && (
						<p className={`mt-2 ${challengeDayTrackingBlockedClass}`} role="status">
							{dayTracking.blocked_message}
						</p>
					)}

					<div className={dayListScrollClass}>
						{editable ? (
							<ul className="space-y-0.5" aria-label="Challenge days">
								{calendarDays.map((isoDate) => (
									<DayCheckRow
										key={isoDate}
										isoDate={isoDate}
										idPrefix={panelId}
										label={formatChallengeDayLabel(isoDate)}
										checked={isDayChecked(dayTracking, isoDate)}
										disabled={isFutureChallengeDay(isoDate, todayIso)}
										pending={isDayCheckPending?.(challenge, isoDate)}
										isToday={isoDate === todayIso}
										onToggle={onDayCheck}
									/>
								))}
							</ul>
						) : (
							<ul className="space-y-1" aria-label="Challenge days">
								{calendarDays.map((isoDate) => {
									const checked = isDayChecked(dayTracking, isoDate);
									return (
										<li
											key={isoDate}
											className={`flex items-center gap-2 px-2 py-1.5 text-sm ${
												checked ? 'text-gray-900 dark:text-dark-text' : 'text-gray-500 dark:text-gray-400'
											}`}
										>
											<i
												className={`fa-solid ${checked ? 'fa-circle-check text-green-600 dark:text-green-400' : 'fa-circle text-gray-300 dark:text-gray-600'} text-xs`}
												aria-hidden="true"
											/>
											<span>{formatChallengeDayLabel(isoDate)}</span>
										</li>
									);
								})}
							</ul>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
